import React, { type FC, type ReactNode, useState, useCallback } from 'react';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Typography, Switch, Collapse, Box, IconButton, Select, MenuItem, Slider, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';

import ConfigOverrideDialog from './ConfigOverrideDialog';
import LabelWithTooltip from './LabelWithTooltip';
import { ClassLevelOverrideOptions } from '../../../../types/adminConfig';

const SliderContainer = styled(Box)({
    padding: '5px 32px 0px 24px',
    width: '800px'
});

const CustomSlider = styled(Slider)({
    color: '#4F46E5',
    height: 6,
    padding: '40px 0',
    '& .MuiSlider-track': {
        height: 6,
        border: 'none'
    },
    '& .MuiSlider-rail': {
        height: 6,
        opacity: 0.2,
        backgroundColor: '#4F46E5'
    },
    '& .MuiSlider-thumb': {
        height: 20,
        width: 20,
        backgroundColor: '#fff',
        '&:hover': {
            boxShadow: '0 0 0 8px rgba(79,70,229,0.2)'
        }
    },
    '& .MuiSlider-mark': {
        backgroundColor: '#999',
        height: 8,
        width: 2,
        top: '50%',
        transform: 'translateY(12px)'
    },
    '& .MuiSlider-markLabel': {
        fontSize: 14,
        color: '#333',
        top: '80%'
    },
    '& .MuiSlider-valueLabel': {
        position: 'absolute',
        backgroundColor: '#4F46E5',
        color: '#fff',
        fontWeight: 600,
        fontSize: 14,
        borderRadius: 6,
        padding: '6px 10px',
        top: -15,
        '& *': {
            background: 'transparent',
            color: 'inherit',
            transform: 'none'
        }
    }
});

const SectionWrapper = styled('div')<{ nested?: boolean }>(({ nested }) => ({
    marginBottom: nested ? '0px' : '24px'
}));

const SectionHeader = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '0px',
    cursor: 'pointer',
    marginBottom: '8px'
});

const FieldList = styled('div')({
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0px'
});

const FieldRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '0px',
    padding: 0,
    margin: 0,
    lineHeight: 1,
    minHeight: '28px'
});

const FieldContentRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: 0,
    padding: 0,
    lineHeight: 1,
    marginLeft: '20px'
});

const FieldContentColumn = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
    marginLeft: '20px',
    padding: 0,
    lineHeight: 1
});

const HighlightedFieldRow = styled(FieldContentRow, {
    shouldForwardProp: (prop) => prop !== 'highlight'
})<{ highlight?: boolean }>(({ theme, highlight }) => ({
    borderRadius: '8px',
    marginLeft: 0,
    ...(highlight && {
        backgroundColor: theme.palette.action.selected,
        boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
        paddingLeft: '8px',
        paddingRight: '8px'
    })
}));

const CollapseContent = styled('div')({
    marginLeft: '40px',
    marginTop: '0px'
});

export type ConfigOverrideOption = {
    key: string;
    label: string;
};

export type ConfigOverrideDialogOption = {
    title: string;
    description: string;
    key?: string;
    statePath?: string;
    onChange?: (key: string, value: any) => void;
    options: ConfigOverrideOption[];
};

type SliderFieldOptions = {
    min: number;
    max: number;
    step?: number;
    marks?: { value: number; label?: string }[];
    valueLabelFormat?: (value: number) => string;
};

export enum ComponentType {
    Switch = 'switch',
    Select = 'select',
    TextField = 'textField',
    Slider = 'slider'
}

export type FieldType = {
    type?: ComponentType;
    label: string;
    key: string;
    statePath?: string;
    disabled?: boolean;
    hidden?: boolean;
    hideSwitch?: boolean; // for switch, if true, the switch will not be shown
    options?: { value: string | number; label?: string; disabled?: boolean }[]; // for dropdown
    sliderOptions?: SliderFieldOptions;
    collapsible?: boolean;
    collapseContent?: string;
    tooltip?: string;
    tooltipLink?: string;
    showNewLabel?: boolean;
    overrideDialogOption?: ConfigOverrideDialogOption;
    children?: FieldType[];
    onChange?: (key: string, value: any) => void;
};

type ConfigSectionProps = {
    title: string | null;
    fields?: FieldType[];
    slots?: Record<string, ReactNode>;
    collapsible?: boolean;
    expanded?: boolean;
    state: any;
    values?: Record<string, any>;
    fieldErrors?: Record<string, any>;
    matchedKeys?: Set<string>;
};

const getLockToolIp = (options, key: string): string | undefined =>
    options?.find((option) => option.key === key)?.label;

const getValueByPath = (obj, path) => (path ? path.split('.').reduce((acc, part) => acc?.[part], obj) : undefined);

const ConfigSection: FC<ConfigSectionProps> = ({
                                                   title,
                                                   fields = [],
                                                   slots = {},
                                                   collapsible = false,
                                                   expanded = true,
                                                   state,
                                                   values = {},
                                                   fieldErrors = {},
                                                   matchedKeys = new Set()
                                               }) => {
    const [open, setOpen] = useState(expanded);
    const [openConfigOverrideDialog, setOpenConfigOverrideDialog] = useState<FieldType | null>(null);

    const handleLockClicked = useCallback((field: FieldType) => {
        setOpenConfigOverrideDialog(field);
    }, []);

    if (fields.length === 0) return null;

    return (
        <SectionWrapper nested={!title}>
            {title && (
                <SectionHeader onClick={() => collapsible && setOpen((prev) => !prev)}>
                    <Typography variant='h6'>{title}</Typography>
                    {collapsible && (
                        <IconButton size='small'>{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                    )}
                </SectionHeader>
            )}
            <Collapse in={!collapsible || open}>
                <FieldList>
                    {fields.map((field) => {
                        if (field.hidden) {
                            return;
                        }
                        const lockKey = field.overrideDialogOption
                            ? (getValueByPath(state, field.overrideDialogOption?.statePath) ??
                                ClassLevelOverrideOptions.NoOne)
                            : undefined;

                        const switchChecked = Boolean(getValueByPath(state, field.statePath) ?? values[field.key]);
                        return (
                            <div key={field.key}>
                                <FieldRow>
                                    {field.type === ComponentType.Switch && (
                                        <HighlightedFieldRow highlight={matchedKeys.has(field.key)}>
                                            {!field.hideSwitch && (
                                                <Switch
                                                    data-analytics-id={`config-switch-${field.key}`}
                                                    id={field.key}
                                                    name={field.key}
                                                    disabled={field.disabled}
                                                    checked={switchChecked}
                                                    onChange={(e) => field?.onChange?.(field.key, e.target.checked)}
                                                />
                                            )}
                                            <LabelWithTooltip
                                                label={field.label}
                                                tooltip={field.tooltip}
                                                tooltipUrl={field.tooltipLink}
                                                lockStatus={lockKey}
                                                lockStatusTooltipUrl={getLockToolIp(
                                                    field.overrideDialogOption?.options,
                                                    lockKey
                                                )}
                                                onLockClicked={() => handleLockClicked(field)}
                                                showNewLabel={field.showNewLabel}
                                            />
                                        </HighlightedFieldRow>
                                    )}

                                    {field.type === ComponentType.Select && (
                                        <FieldContentRow>
                                            <Typography>{field.label}</Typography>
                                            <Select
                                                data-analytics-id={`config-select-${field.key}`}
                                                id={field.key}
                                                name={field.key}
                                                value={getValueByPath(state, field.statePath)}
                                                onChange={(e) => field?.onChange?.(field.key, e.target.value)}
                                                size='small'
                                                style={{ minWidth: '260px' }}
                                            >
                                                {field.options?.map((opt) => (
                                                    <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                                                        {opt.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FieldContentRow>
                                    )}

                                    {field.type === ComponentType.TextField && (
                                        <FieldContentColumn>
                                            <LabelWithTooltip
                                                label={field.label}
                                                tooltip={field.tooltip}
                                                tooltipUrl={field.tooltipLink}
                                                lockStatus={lockKey}
                                                lockStatusTooltipUrl={getLockToolIp(
                                                    field.overrideDialogOption?.options,
                                                    lockKey
                                                )}
                                                onLockClicked={() => handleLockClicked(field)}
                                                showNewLabel={field.showNewLabel}
                                            />
                                            <TextField
                                                data-analytics-id={`config-textfield-${field.key}`}
                                                id={field.key}
                                                name={field.key}
                                                value={(() => {
                                                    const rawValue = getValueByPath(state, field.statePath);
                                                    return Array.isArray(rawValue)
                                                        ? rawValue.join(', ').trim()
                                                        : (rawValue ?? '');
                                                })()}
                                                onChange={(e) => field?.onChange?.(field.key, e.target.value)}
                                                error={Boolean(fieldErrors[field.key])}
                                                helperText={fieldErrors[field.key]}
                                                size='small'
                                                style={{minWidth: '260px' }}
                                            />
                                        </FieldContentColumn>
                                    )}

                                    {field.type === ComponentType.Slider && (
                                        <SliderContainer>
                                            {field.label && <Typography>{field.label}</Typography>}
                                            <CustomSlider
                                                data-analytics-id={`config-slider-${field.key}`}
                                                id={field.key}
                                                name={field.key}
                                                value={getValueByPath(state, field.statePath)}
                                                onChange={(e, newValue) => field?.onChange?.(field.key, newValue)}
                                                min={field.sliderOptions?.min ?? 0}
                                                max={field.sliderOptions?.max ?? 100}
                                                step={field.sliderOptions?.step ?? null}
                                                marks={field.sliderOptions?.marks}
                                                valueLabelDisplay='auto'
                                                valueLabelFormat={field.sliderOptions?.valueLabelFormat ?? undefined}
                                                getAriaLabel={field.sliderOptions?.valueLabelFormat ?? undefined}
                                            />
                                        </SliderContainer>
                                    )}
                                </FieldRow>

                                {field.children && switchChecked && (
                                    <CollapseContent>
                                        <ConfigSection
                                            title={null} // Nested section doesn't need a title
                                            fields={field.children}
                                            slots={slots}
                                            collapsible={false}
                                            state={state}
                                            values={values}
                                            fieldErrors={fieldErrors}
                                            matchedKeys={matchedKeys}
                                        />
                                    </CollapseContent>
                                )}

                                {field.collapsible && field.collapseContent && slots?.[field.collapseContent] && (
                                    <CollapseContent>{slots[field.collapseContent]}</CollapseContent>
                                )}
                            </div>
                        );
                    })}
                </FieldList>
            </Collapse>
            {openConfigOverrideDialog && openConfigOverrideDialog.overrideDialogOption && (
                <ConfigOverrideDialog
                    open={Boolean(openConfigOverrideDialog)}
                    selected={
                        getValueByPath(state, openConfigOverrideDialog.overrideDialogOption.statePath) ??
                        ClassLevelOverrideOptions.NoOne
                    }
                    onClose={() => setOpenConfigOverrideDialog(null)}
                    onSelect={(key) => {
                        if (
                            openConfigOverrideDialog?.overrideDialogOption?.onChange &&
                            openConfigOverrideDialog.overrideDialogOption.key
                        ) {
                            openConfigOverrideDialog.overrideDialogOption.onChange?.(
                                openConfigOverrideDialog.overrideDialogOption.key,
                                key ?? ClassLevelOverrideOptions.NoOne
                            );
                        }
                        setOpenConfigOverrideDialog(null);
                    }}
                    configOverrideOption={openConfigOverrideDialog.overrideDialogOption}
                />
            )}
        </SectionWrapper>
    );
};
export default ConfigSection;
// This file defines a reusable ConfigSection component that renders a section of configuration settings.
